/**
 * Boxing Ring Configurator - Modular JavaScript
 * Compact, efficient, and well-organized
 */

class BoxingRingConfigurator {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.root = document.getElementById(`boxing-ring-${sectionId}`);
    this.config = {
      customText: 'YOUR NAME',
      customLogo: null
    };
    this.dynamicOptions = {};
    this.tdaReady = false;
    this.productType = 'ring'; // default to ring
    
    if (!this.root) {
      console.error('Configurator root element not found');
      return;
    }
    
    // Detect product type based on page URL or TDA elements
    this.detectProductType();
    
    this.init();
  }
  
  detectProductType() {
    // Check product title first for specific heavy bag condition
    const titleElement = this.root?.querySelector('.viewer__title');
    if (titleElement) {
      const productTitle = titleElement.textContent.toLowerCase();
      console.log('🔍 Checking product title:', productTitle);
      
      // Check if title contains "heavy bag" and is NOT ring-related
      if (productTitle.includes('heavy bag') && !productTitle.includes('ring')) {
        this.productType = 'heavy-bag';
        console.log('🥊 Detected product type: HEAVY BAG (special pricing)');
        console.log(`✅ Final product type: ${this.productType.toUpperCase()}`);
        return;
      }
    }
    
    // Check URL for product type indicators
    const url = window.location.href.toLowerCase();
    
    if (url.includes('bag') || url.includes('punching')) {
      this.productType = 'bag';
      console.log('🥊 Detected product type: BAG');
    } else if (url.includes('ring')) {
      this.productType = 'ring';
      console.log('🥊 Detected product type: RING');
    } else {
      // Check TDA elements for product type
      const tdaElements = document.querySelectorAll('[id*="tda-customizer-customization-option-"]');
      for (const element of tdaElements) {
        const titleElement = element.querySelector('.tda-customizer-customization-option-title');
        if (titleElement) {
          const title = titleElement.textContent.toLowerCase();
          if (title.includes('panel') || title.includes('strap') || title.includes('trim')) {
            this.productType = 'bag';
            console.log('🥊 Detected product type: BAG (from TDA elements)');
            break;
          } else if (title.includes('ring') || title.includes('corner') || title.includes('rope')) {
            this.productType = 'ring';
            console.log('🥊 Detected product type: RING (from TDA elements)');
            break;
          }
        }
      }
    }
    
    console.log(`✅ Final product type: ${this.productType.toUpperCase()}`);
  }
  
  async init() {
    console.log('🚀 Initializing Boxing Ring Configurator...');
    
    // Initialize modules
    this.tda = new TDAIntegration(this);
    this.ui = new UIManager(this);
    this.colors = new ColorManager(this);
    this.logoEditor = new LogoEditor(this);
    
    // Hide TDA UI immediately
    this.tda.hideUI();
    
    // Setup event listeners
    this.setupEventListeners();
    
    // Wait for TDA and integrate
    await this.tda.waitForApp();
    
    // Initialize text field
    this.ui.initializeTextField();
    
    // Setup logo upload
    this.ui.setupLogoUpload();
    
    // Setup price synchronization
    this.ui.setupPriceSync();
    
    // Sync initial state
    this.syncInitialState();
    
    // Update summary
    this.ui.updateSummary();
    
    console.log('✅ Configurator initialized successfully');
  }
  
  setupEventListeners() {
    const { root } = this;
    
    // Summary toggle
    const summaryToggle = root.querySelector('#summary-toggle');
    const summaryContent = root.querySelector('#summary-content');
    if (summaryToggle && summaryContent) {
      summaryToggle.addEventListener('click', () => {
        const expanded = summaryToggle.getAttribute('aria-expanded') === 'true';
        summaryToggle.setAttribute('aria-expanded', !expanded);
        summaryContent.classList.toggle('expanded', !expanded);
      });
    }
    
    // Add to cart
    const addToCartBtn = root.querySelector('[data-action="add-to-cart"]');
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', () => this.addToCart());
    }
    
    // Debug button (design mode only)
    const debugBtn = root.querySelector('[data-action="debug-all"]');
    if (debugBtn) {
      debugBtn.addEventListener('click', () => this.runDebugTests());
    }
  }
  
  syncInitialState() {
    // Sync TDA sections
    const tdaSections = this.root.querySelectorAll('.color-section[data-tda-section="true"]');
    tdaSections.forEach(section => {
      const sectionKey = section.getAttribute('data-section-key');
      const selectedOption = section.querySelector('.color-swatch.selected');
      if (selectedOption && sectionKey) {
        this.config[sectionKey] = selectedOption.dataset.color;
      }
    });
    
    // Sync text
    const textInput = this.root.querySelector('#custom-text');
    if (textInput?.value) {
      this.config.customText = textInput.value;
    }
    
    console.log('📌 Initial state synced:', this.config);
  }
  
  async addToCart() {
    console.log('🛒 Add to cart initiated...');
    
    // Check if configurator is properly initialized
    if (!this.root) {
      console.error('❌ Configurator root not found');
      this.showCartError('Configuration error - please refresh the page');
      return;
    }
    
    // Try TDA first
    if (this.tdaReady) {
      const tdaBtn = document.querySelector('[id*="tda-customizer-footer-atc-btn-"]');
      if (tdaBtn) {
        console.log('🛒 Using TDA add to cart');
        tdaBtn.click();
        return;
      }
    }
    
    // Prepare cart data with configuration
    let cartData;
    try {
      cartData = this.prepareCartData();
      console.log('📦 Prepared cart data:', cartData);
      
      // Validate cart data
      if (!cartData.id || cartData.id === '123456789') {
        throw new Error('Invalid product variant ID. Please select a product variant.');
      }
      
    } catch (error) {
      console.error('❌ Failed to prepare cart data:', error);
      this.showCartError('Configuration error: ' + error.message);
      return;
    }
    
    const addToCartBtn = this.root.querySelector('[data-action="add-to-cart"]');
    
    try {
      console.log('🛒 Adding to cart with configuration:', cartData);
      
      // Show loading state
      if (addToCartBtn) {
        addToCartBtn.disabled = true;
        addToCartBtn.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="loading-spinner">
            <path d="M21 12a9 9 0 0 1-9 9m9-9a9 9 0 0 0-9-9m9 9H3m9 9v-9m0-9v9"/>
          </svg>
          Adding...
        `;
      }
      
      // Add the product to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(cartData)
      });
      
      const responseText = await response.text();
      console.log('🔍 Cart API response status:', response.status);
      console.log('🔍 Cart API response text:', responseText);
      
      if (response.ok) {
        let result;
        try {
          result = JSON.parse(responseText);
        } catch (parseError) {
          console.error('❌ Failed to parse cart response:', parseError);
          throw new Error('Invalid response from cart API');
        }
        
        console.log('✅ Added to cart successfully:', result);
        
        // Show success feedback
        this.showCartSuccess();
        
        // Update cart drawer if it exists
        this.updateCartDrawer();
        
      } else {
        let errorMessage = 'Failed to add to cart';
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.message || errorData.description || errorMessage;
        } catch (parseError) {
          errorMessage = responseText || errorMessage;
        }
        throw new Error(`${errorMessage} (Status: ${response.status})`);
      }
      
    } catch (error) {
      console.error('❌ Add to cart failed:', error);
      
      // Show error feedback with more specific message
      let userMessage = error.message;
      if (error.message.includes('422')) {
        userMessage = 'Product configuration error. Please check your selections.';
      } else if (error.message.includes('404')) {
        userMessage = 'Product not found. Please refresh and try again.';
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        userMessage = 'Network error. Please check your connection and try again.';
      }
      
      this.showCartError(userMessage);
      
    } finally {
      // Reset button state
      if (addToCartBtn) {
        addToCartBtn.disabled = false;
        addToCartBtn.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="9" cy="21" r="1"></circle>
            <circle cx="20" cy="21" r="1"></circle>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
          </svg>
          Add to Cart
        `;
      }
    }
  }
  

  
  prepareCartData() {
    // Get current product variant ID (you might need to adjust this based on your product setup)
    const variantId = this.getProductVariantId();
    
    // Prepare line item properties (only for product-specific customizations)
    const properties = {};
    
    // Custom Logo (product-specific customization)
    const logoEnabled = this.root.querySelector('#logo-enabled');
    const logoPrice = (this.productType === 'bag' || this.productType === 'heavy-bag') ? '$65' : '$1,850';
    const logoPriceFormatted = (this.productType === 'bag' || this.productType === 'heavy-bag') ? '$65.00' : '$1,850.00';
    
    if (logoEnabled && logoEnabled.checked) {
      if (this.config.customLogo) {
        properties['Custom Logo'] = `Yes - Logo uploaded (+${logoPrice})`;
        properties['_logo_data'] = this.config.customLogo; // Base64 or URL for processing
      } else {
        properties['Custom Logo'] = `Yes - No logo uploaded (+${logoPrice})`;
      }
      properties['_logo_pricing'] = logoPriceFormatted;
    } else {
      properties['Custom Logo'] = 'No - Logo disabled';
    }
    
    // Color Selections (product-specific)
    const colorConfig = [];
    const tdaSections = this.root.querySelectorAll('.color-section[data-tda-section="true"]');
    tdaSections.forEach(section => {
      const sectionKey = section.getAttribute('data-section-key');
      const selectedOption = section.querySelector('.color-swatch.selected');
      const colorLabel = section.querySelector('.color-label span:first-child');
      
      if (selectedOption && colorLabel && sectionKey) {
        const colorName = selectedOption.dataset.name || selectedOption.dataset.color;
        const componentName = colorLabel.textContent;
        
        properties[componentName] = colorName;
        colorConfig.push(`${componentName}: ${colorName}`);
      }
    });
    
    // Color summary for easy reference
    if (colorConfig.length > 0) {
      properties['_color_summary'] = colorConfig.join(', ');
    }
    
    // Product configuration timestamp
    properties['_configured_at'] = new Date().toISOString();
    
    return {
      id: variantId,
      quantity: 1,
      properties: properties
    };
  }
  
  getProductVariantId() {
    console.log('🔍 Looking for product variant ID...');
    
    // Try to get variant ID from various sources
    let variantId = null;
    
    // Check if there's a product form
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const variantInput = productForm.querySelector('[name="id"]');
      if (variantInput && variantInput.value) {
        variantId = variantInput.value;
        console.log('✅ Found variant ID in product form:', variantId);
      }
    }
    
    // Check meta tags
    if (!variantId) {
      const metaVariant = document.querySelector('meta[name="product-variant-id"]');
      if (metaVariant && metaVariant.content) {
        variantId = metaVariant.content;
        console.log('✅ Found variant ID in meta tag:', variantId);
      }
    }
    
    // Check window variables (common Shopify pattern)
    if (!variantId && window.product && window.product.selected_or_first_available_variant) {
      variantId = window.product.selected_or_first_available_variant.id;
      console.log('✅ Found variant ID in window.product:', variantId);
    }
    
    // Check ShopifyAnalytics
    if (!variantId && window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
      const productData = window.ShopifyAnalytics.meta.product;
      if (productData.variants && productData.variants.length > 0) {
        variantId = productData.variants[0].id;
        console.log('✅ Found variant ID in ShopifyAnalytics:', variantId);
      }
    }
    
    // Check for liquid-generated script tags with product data
    if (!variantId) {
      const scriptTags = document.querySelectorAll('script[type="application/json"]');
      for (const script of scriptTags) {
        try {
          const data = JSON.parse(script.textContent);
          if (data.id && data.title && data.variants) {
            // This looks like product data
            const firstVariant = data.variants[0];
            if (firstVariant && firstVariant.id) {
              variantId = firstVariant.id;
              console.log('✅ Found variant ID in JSON script tag:', variantId);
              break;
            }
          }
        } catch (error) {
          // Skip invalid JSON
        }
      }
    }
    
    // Try to extract from URL (for product pages)
    if (!variantId) {
      const urlParams = new URLSearchParams(window.location.search);
      const variantParam = urlParams.get('variant');
      if (variantParam) {
        variantId = variantParam;
        console.log('✅ Found variant ID in URL params:', variantId);
      }
    }
    
    // Debug output
    if (!variantId) {
      console.error('❌ No variant ID found in any source');
      console.log('Available sources checked:');
      console.log('- Product form:', !!productForm);
      console.log('- Meta tag:', !!document.querySelector('meta[name="product-variant-id"]'));
      console.log('- Window.product:', !!window.product);
      console.log('- ShopifyAnalytics:', !!window.ShopifyAnalytics);
      console.log('- URL params:', new URLSearchParams(window.location.search).get('variant'));
      
      // Log current page type
      console.log('Current page info:');
      console.log('- URL:', window.location.href);
      console.log('- Page type:', document.body.className);
      
      throw new Error('Product variant ID not found. This configurator needs to be on a product page or have variant data available.');
    }
    
    console.log('🎯 Final variant ID:', variantId);
    return variantId;
  }
  
  showCartSuccess() {
    // Create success notification
    const notification = document.createElement('div');
    notification.className = 'cart-notification cart-notification--success';
    notification.innerHTML = `
      <div class="cart-notification__content">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22,4 12,14.01 9,11.01"></polyline>
        </svg>
        <span>Successfully added to cart with your customizations!</span>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    // Remove after 3 seconds
    setTimeout(() => {
      notification.remove();
    }, 3000);
  }
  
  showCartError(message) {
    // Create error notification
    const notification = document.createElement('div');
    notification.className = 'cart-notification cart-notification--error';
    notification.innerHTML = `
      <div class="cart-notification__content">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
        <span>Failed to add to cart: ${message}</span>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    // Remove after 5 seconds
    setTimeout(() => {
      notification.remove();
    }, 5000);
  }
  
  updateCartDrawer() {
    // Try to update cart drawer if it exists
    const cartDrawer = document.querySelector('[data-cart-drawer]');
    if (cartDrawer && typeof window.updateCartDrawer === 'function') {
      window.updateCartDrawer();
    }
    
    // Try to update cart bubble count
    const cartBubble = document.querySelector('[data-cart-count]');
    if (cartBubble) {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          cartBubble.textContent = cart.item_count;
        })
        .catch(error => console.log('Cart count update failed:', error));
    }
  }
  
  runDebugTests() {
    console.log('🧪 Running debug tests...');
    this.tda.debug();
    this.ui.testTextField();
    this.ui.testLogoUpload();
    this.logoEditor.test();
  }
}

/**
 * TDA Integration Module
 */
class TDAIntegration {
  constructor(configurator) {
    this.configurator = configurator;
    this.maxAttempts = 50;
    this.attempts = 0;
  }
  
  async waitForApp() {
    return new Promise((resolve) => {
      const checkReady = () => {
        this.attempts++;
        
        const viewer = document.querySelector('[id*="tda-app-section-model-viewer-"]');
        const container = document.querySelector('[id*="tda-app-section-model-viewer-container-"]');
        const optionsContainer = document.querySelector('.tda-customizer-customization-options-container');
        const customizationOptions = document.querySelectorAll('.tda-customizer-customization-option');
        
        console.log(`TDA Check ${this.attempts}:`, {
          viewer: !!viewer,
          container: !!container,
          options: !!optionsContainer,
          customizations: customizationOptions.length
        });
        
        if (viewer && container) {
          console.log('✅ TDA app ready');
          this.configurator.tdaReady = true;
          this.integrateViewer();
          
          // Always try to detect customizations, even if options container isn't ready yet
          this.detectCustomizations();
          resolve();
          return;
        }
        
        if (this.attempts < this.maxAttempts) {
          setTimeout(checkReady, 200); // Increased interval
        } else {
          console.log('⚠️ TDA timeout - proceeding anyway');
          // Still try to detect customizations on timeout
          this.detectCustomizations();
          resolve();
        }
      };
      
      checkReady();
    });
  }
  
  integrateViewer() {
    const viewer = document.querySelector('[id*="tda-app-section-model-viewer-"]');
    const container = document.querySelector('[id*="tda-app-section-model-viewer-container-"]');
    const stickyContainer = document.querySelector('[id*="tda-app-section-model-viewer-sticky-container-"]');
    const placeholder = this.configurator.root.querySelector('#viewer-placeholder');
    
    if (!viewer || !placeholder) {
      console.error('❌ Cannot integrate viewer');
      return;
    }
    
    console.log('🔄 Integrating 3D viewer...');
    
    // Clear placeholder content first
    placeholder.innerHTML = '';
    
    // Move the entire container hierarchy to our placeholder
    const elementToMove = container || viewer;
    placeholder.appendChild(elementToMove);
    
    // Apply integration styles with force
    if (container) {
      container.classList.add('moved-to-custom');
      Object.assign(container.style, {
        width: '100%',
        height: '100%',
        maxWidth: '100%',
        maxHeight: '100%',
        minWidth: '300px',
        minHeight: '300px',
        position: 'relative',
        pointerEvents: 'auto',
        touchAction: 'auto',
        zIndex: '10',
        display: 'block',
        visibility: 'visible',
        opacity: '1'
      });
      
      // Override any inline styles that might be interfering
      container.style.setProperty('width', '100%', 'important');
      container.style.setProperty('height', '100%', 'important');
      container.style.setProperty('flex-grow', '1', 'important');
    }
    
    if (stickyContainer) {
      Object.assign(stickyContainer.style, {
        width: '100%',
        height: '100%',
        background: 'transparent',
        position: 'relative',
        zIndex: '10'
      });
      
      stickyContainer.style.setProperty('width', '100%', 'important');
      stickyContainer.style.setProperty('height', '100%', 'important');
    }
    
    // Apply viewer styles with maximum compatibility
    viewer.classList.add('moved-to-custom');
    Object.assign(viewer.style, {
      width: '100%',
      height: '100%',
      maxWidth: '100%',
      maxHeight: '100%',
      minWidth: '300px',
      minHeight: '300px',
      position: 'relative',
      pointerEvents: 'auto',
      touchAction: 'auto',
      zIndex: '10',
      backgroundColor: 'transparent',
      display: 'block',
      visibility: 'visible',
      opacity: '1'
    });
    
    // Force override any conflicting styles
    viewer.style.setProperty('width', '100%', 'important');
    viewer.style.setProperty('height', '100%', 'important');
    
    // Ensure camera controls are enabled
    if (!viewer.hasAttribute('camera-controls')) {
      viewer.setAttribute('camera-controls', '');
    }
    if (!viewer.hasAttribute('touch-action')) {
      viewer.setAttribute('touch-action', 'pan-y');
    }
    
    // Hide any loading overlays that might block the view
    const loadingElement = document.querySelector('[id*="model-loading-"]');
    if (loadingElement) {
      loadingElement.style.display = 'none';
      loadingElement.style.opacity = '0';
    }
    
    // Update container state and hide placeholder content
    const viewerContainer = this.configurator.root.querySelector('.viewer__container');
    if (viewerContainer) {
      viewerContainer.classList.remove('viewer-loading');
      viewerContainer.classList.add('viewer-loaded', 'loaded');
    }
    
    // Hide placeholder content and adjust layout (fallback for older browsers)
    placeholder.classList.add('has-3d-viewer');
    const placeholderContent = placeholder.querySelector('.placeholder__content');
    if (placeholderContent) {
      placeholderContent.style.display = 'none';
    }
    
    // Set placeholder to allow interaction with 3D viewer
    placeholder.style.setProperty('pointer-events', 'auto', 'important');
    placeholder.style.setProperty('z-index', '10', 'important');
    
    // Force a reflow and trigger model-viewer resize
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
      
      // Force model-viewer to update its dimensions
      if (viewer.updateComplete) {
        viewer.updateComplete.then(() => {
          console.log('✅ Model viewer ready and sized properly');
        });
      }
      
      // Additional fallback resize
      if (viewer.resize && typeof viewer.resize === 'function') {
        viewer.resize();
      }
    }, 100);
    
    console.log('✅ 3D viewer integrated and configured');
  }
  
  detectCustomizations() {
    console.log('🔍 Detecting TDA customizations...');
    
    // Wait a bit for TDA to fully load
    setTimeout(() => {
      const tdaOptions = document.querySelectorAll('.tda-customizer-customization-option');
      const detectedOptions = [];
      
      console.log(`Found ${tdaOptions.length} TDA customization options`);
      
      tdaOptions.forEach(option => {
        const titleElement = option.querySelector('.tda-customizer-customization-option-title');
        const colorSwatches = option.querySelectorAll('.tda-customizer-customization-option-value');
        
        if (titleElement && colorSwatches.length > 0) {
          const title = titleElement.textContent.trim();
          const colors = Array.from(colorSwatches).map(swatch => {
            // Enhanced color name detection with multiple fallback methods
            let colorName = 'Unknown';
            let detectedColor = null;
            
            // Method 1: Try TDA tooltip text (most reliable for TDA elements)
            const tooltipElement = swatch.querySelector('.tda-tooltiptext span');
            if (tooltipElement && tooltipElement.textContent.trim()) {
              const tooltipText = tooltipElement.textContent.trim();
              // Extract color name, removing price info like "(+US$200)"
              colorName = tooltipText.replace(/\s*\([^)]*\)$/, '');
              
              // Check if this swatch has an image in the tooltip (for textures like camo)
              const tooltipImg = swatch.querySelector('.tda-tooltiptext img');
              if (tooltipImg && tooltipImg.src) {
                detectedColor = tooltipImg.src;
                console.log(`🖼️ Found texture image in tooltip: ${detectedColor}`);
              }
              
              console.log(`🏷️ Extracted from tooltip: "${tooltipText}" -> "${colorName}"`);
            } else {
              console.log(`⚠️ No tooltip found for swatch: ${swatch.id}`);
            }
            
            // Fallback methods if we still don't have a color name
            if (colorName === 'Unknown') {
              // Method 2: Try title div inside swatch
              const titleDiv = swatch.querySelector('.tda-customizer-customization-option-value-title');
              if (titleDiv && titleDiv.textContent.trim()) {
                colorName = titleDiv.textContent.trim();
              } else {
                // Method 3: Try data attributes
                if (swatch.dataset.title) {
                  colorName = swatch.dataset.title;
                } else if (swatch.getAttribute('title')) {
                  colorName = swatch.getAttribute('title');
                } else if (swatch.getAttribute('aria-label')) {
                  colorName = swatch.getAttribute('aria-label');
                } else {
                  // Method 4: Try to extract from computed styles
                  const computedStyle = window.getComputedStyle(swatch);
                  const backgroundColor = computedStyle.backgroundColor;
                  
                  // Also check background image first for texture patterns
                  const backgroundImage = computedStyle.backgroundImage;
                  if (backgroundImage && backgroundImage !== 'none' && !detectedColor) {
                    // Check for texture images first (this catches TDA texture backgrounds)
                    const urlMatch = backgroundImage.match(/url\(['"]*([^'"]*\.(jpg|png|jpeg))['"]*\)/i);
                    if (urlMatch) {
                      detectedColor = urlMatch[1];
                      const filename = urlMatch[1].split('/').pop();
                      
                      // Special handling for texture patterns
                      if (filename.includes('camo')) {
                        colorName = 'Camo';
                      } else if (filename.includes('gold')) {
                        colorName = 'Gold';
                      } else if (filename.includes('platinum') || filename.includes('silver')) {
                        colorName = 'Platinum';
                      }
                      
                      console.log(`🎨 Found texture in computed style: ${filename} -> ${colorName}`);
                    }
                  }
                  
                  // Fall back to background color if no texture found
                  if (!detectedColor && backgroundColor && backgroundColor !== 'rgba(0, 0, 0, 0)' && backgroundColor !== 'transparent') {
                    detectedColor = backgroundColor;
                    colorName = this.getColorNameFromRGB(backgroundColor);
                  }
                  
                  // Method 5: Try background gradients for solid colors if still unknown
                  if (colorName === 'Unknown' && !detectedColor) {
                    const backgroundImage = computedStyle.backgroundImage;
                    if (backgroundImage && backgroundImage !== 'none') {
                      // Check for linear gradients (TDA format: linear-gradient(to right, rgb(150, 52, 51), ...))
                      const gradientMatch = backgroundImage.match(/linear-gradient\([^,]+,\s*rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
                      if (gradientMatch) {
                        const r = parseInt(gradientMatch[1]);
                        const g = parseInt(gradientMatch[2]);
                        const b = parseInt(gradientMatch[3]);
                        detectedColor = `rgb(${r}, ${g}, ${b})`;
                        colorName = this.getColorNameFromRGB(detectedColor);
                        console.log(`🎨 Extracted from gradient: ${detectedColor} -> ${colorName}`);
                      }
                    }
                  }
                  
                  // Method 6: Try inline styles
                  if (colorName === 'Unknown') {
                    const inlineStyle = swatch.getAttribute('style');
                    if (inlineStyle) {
                      const colorMatch = inlineStyle.match(/background-color:\s*([^;]+)/);
                      if (colorMatch) {
                        detectedColor = colorMatch[1].trim();
                        colorName = this.getColorNameFromRGB(detectedColor);
                      }
                    }
                  }
                  
                  // Method 7: Extract from classes
                  if (colorName === 'Unknown') {
                    const classList = swatch.className;
                    const colorMatch = classList.match(/color-([a-zA-Z-]+)/);
                    if (colorMatch) {
                      colorName = colorMatch[1].replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                    }
                  }
                }
              }
            }
            
            const id = swatch.id.replace('tda-customizer-customization-option-value-', '');
            const isSelected = swatch.classList.contains('tda-customizer-customization-option-value-selected');
            
            console.log(`🎨 Detected color: ${colorName} (ID: ${id}) ${detectedColor ? `[${detectedColor}]` : ''}`);
            
            // Determine if this is a texture/image-based color
            const isTexture = detectedColor && (detectedColor.startsWith('http') || detectedColor.includes('.jpg') || detectedColor.includes('.png'));
            
            return {
              name: colorName,
              id: id,
              selected: isSelected,
              element: swatch,
              computedColor: detectedColor,
              isTexture: isTexture,
              textureUrl: isTexture ? detectedColor : null
            };
          });
          
          detectedOptions.push({ title, colors, element: option });
          console.log(`✅ Detected option: ${title} with ${colors.length} colors`);
        }
      });
      
      if (detectedOptions.length > 0) {
        this.configurator.colors.createDynamicSections(detectedOptions);
        this.hideLoadingState();
        
        // Logo detection no longer needed - direct sync works
        
      } else {
        console.log('⚠️ No TDA options detected, retrying...');
        // Retry detection after another delay
        setTimeout(() => this.detectCustomizations(), 2000);
      }
      
      console.log(`✅ Total detected ${detectedOptions.length} customization options`);
    }, 1000);
  }
  
  hideLoadingState() {
    const loadingState = this.configurator.root.querySelector('#color-loading');
    if (loadingState) {
      loadingState.classList.add('hidden');
      loadingState.style.display = 'none';
      console.log('✅ Loading state hidden');
    }
  }
  
  hideUI() {
    // Hide TDA UI elements
    const selectors = [
      '[id*="tda-customizer-app-section-container-"]:not([id*="model-viewer"])',
      '[id*="tda-customizer-controls-container-"]',
      '[id*="tda-customizer-footer-"]',
      '[id*="tda-customizer-customization-options-container-"]',
      '[class*="tda-customizer-wrapper"]'
    ];
    
    selectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(el => {
        el.style.display = 'none';
        el.style.visibility = 'hidden';
        el.style.opacity = '0';
      });
    });
  }
  
  getColorNameFromRGB(rgb) {
    // Convert RGB to approximate color name - Enhanced for TDA colors
    const colorMap = {
      // Exact matches for common TDA colors
      'rgb(0, 0, 0)': 'Black',
      'rgb(255, 255, 255)': 'White', 
      'rgb(150, 52, 51)': 'Red',
      'rgb(236, 94, 56)': 'Orange',
      'rgb(60, 48, 112)': 'Purple',
      'rgb(33, 78, 73)': 'Green',
      'rgb(26, 34, 73)': 'Royal Blue',
      'rgb(252, 183, 44)': 'Yellow',
      'rgb(127, 129, 126)': 'Gray',
      'rgb(97, 80, 64)': 'Brown',
      'rgb(46, 163, 85)': 'Lime Green',
      'rgb(99, 47, 49)': 'Burgundy',
      // Common variations
      'rgb(100, 49, 51)': 'Burgundy',
      'rgb(98, 48, 50)': 'Burgundy',
      'rgb(102, 84, 66)': 'Brown',
      // Standard colors
      'rgb(255, 0, 0)': 'Red',
      'rgb(0, 255, 0)': 'Green',
      'rgb(0, 0, 255)': 'Blue',
      'rgb(255, 255, 0)': 'Yellow',
      'rgb(255, 165, 0)': 'Orange',
      'rgb(128, 0, 128)': 'Purple',
      'rgb(255, 215, 0)': 'Gold',
      'rgb(192, 192, 192)': 'Silver'
    };
    
    // Check exact match first
    if (colorMap[rgb]) {
      return colorMap[rgb];
    }
    
    // Parse RGB values for approximate matching
    const rgbMatch = rgb.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (rgbMatch) {
      const r = parseInt(rgbMatch[1]);
      const g = parseInt(rgbMatch[2]);
      const b = parseInt(rgbMatch[3]);
      
      // Enhanced color approximation logic
      if (r > 240 && g > 240 && b > 240) return 'White';
      if (r < 30 && g < 30 && b < 30) return 'Black';
      
      // Red family
      if (r > 120 && g < 80 && b < 80) return 'Red';
      if (r > 90 && r < 160 && g > 40 && g < 60 && b > 40 && b < 60) return 'Burgundy';
      
      // Orange family  
      if (r > 200 && g > 80 && g < 120 && b < 80) return 'Orange';
      
      // Yellow family
      if (r > 220 && g > 150 && b < 80) return 'Yellow';
      
      // Green family
      if (r < 80 && g > 120 && b < 100) return 'Green';
      if (r > 40 && r < 60 && g > 140 && g < 180 && b > 80 && b < 100) return 'Lime Green';
      if (r < 50 && g < 100 && b < 90) return 'Green';
      
      // Blue family
      if (r < 80 && g < 100 && b > 120) return 'Blue';
      if (r < 40 && g < 50 && b > 60) return 'Royal Blue';
      
      // Purple family
      if (r > 50 && r < 80 && g < 60 && b > 90) return 'Purple';
      
      // Gray family
      if (Math.abs(r - g) < 20 && Math.abs(g - b) < 20 && Math.abs(r - b) < 20) {
        if (r > 100 && r < 150) return 'Gray';
      }
      
      // Brown family
      if (r > 80 && r < 120 && g > 60 && g < 100 && b > 50 && b < 80) return 'Brown';
    }
    
    return 'Custom';
  }

  triggerColorChange(tdaId) {
    // Find the TDA element by its actual ID
    const element = document.getElementById(`tda-customizer-customization-option-value-${tdaId}`);
    if (element) {
      console.log(`🎨 Triggering TDA color: ${tdaId}`);
      element.click();
      return true;
    } else {
      console.warn(`⚠️ TDA color element not found: tda-customizer-customization-option-value-${tdaId}`);
      // Debug: list available elements
      const allTDAElements = document.querySelectorAll('[id*="tda-customizer-customization-option-value-"]');
      console.log('Available TDA elements:', Array.from(allTDAElements).map(el => el.id));
      return false;
    }
  }
  
  syncText(value) {
    const tdaInputs = document.querySelectorAll('input[id*="tda-customizer-customization-option-value-custom-input-"]');
    tdaInputs.forEach(input => {
      if (input.value !== value) {
        input.value = value;
        ['input', 'change'].forEach(event => {
          input.dispatchEvent(new Event(event, { bubbles: true }));
        });
      }
    });
  }
  
  syncTDALogoOption(value) {
    // TDA uses file upload interface, not dropdown - this is for reference only
    console.log(`🔄 TDA logo option reference: ${value}`);
    
    // For TDA file upload interface, we need to handle it differently
    // The actual syncing happens through file uploads via syncLogo()
    return true;
  }
  

  
  syncLogo(file) {
    const tdaInput = document.querySelector('input[id*="tda-file-upload-input-"]');
    if (tdaInput) {
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      tdaInput.files = dataTransfer.files;
      tdaInput.dispatchEvent(new Event('change', { bubbles: true }));
      console.log('✅ Logo synced with TDA');
    }
  }

  clearLogo() {
    const tdaInput = document.querySelector('input[id*="tda-file-upload-input-"]');
    if (tdaInput) {
      // Clear the file input
      tdaInput.value = '';
      tdaInput.files = new DataTransfer().files;
      tdaInput.dispatchEvent(new Event('change', { bubbles: true }));
      
      // Also try to find and click any remove/clear buttons in TDA
      const removeButtons = document.querySelectorAll('.tda-customizer-customization-option [class*="remove"], .tda-customizer-customization-option [class*="delete"], .tda-customizer-customization-option [class*="clear"]');
      for (const button of removeButtons) {
        if (button.offsetParent !== null) { // Check if visible
          button.click();
          break;
        }
      }
      
      console.log('✅ Logo cleared from TDA');
      return true;
    }
    console.log('⚠️ TDA input not found for logo clearing');
    return false;
  }
  
  debug() {
    const elements = document.querySelectorAll('[id*="tda-"], [class*="tda-"]');
    const customizationOptions = document.querySelectorAll('.tda-customizer-customization-option');
    const colorElements = document.querySelectorAll('.tda-customizer-customization-option-value');
    
    console.log('🔍 TDA Debug Information:');
    console.log(`Total TDA elements: ${elements.length}`);
    console.log(`Customization options: ${customizationOptions.length}`);
    console.log(`Color elements: ${colorElements.length}`);
    
    console.log('Customization options details:', 
      Array.from(customizationOptions).map(option => ({
        title: option.querySelector('.tda-customizer-customization-option-title')?.textContent,
        colors: Array.from(option.querySelectorAll('.tda-customizer-customization-option-value')).map(color => ({
          id: color.id,
          name: color.querySelector('.tda-customizer-customization-option-value-title')?.textContent,
          selected: color.classList.contains('tda-customizer-customization-option-value-selected')
        }))
      }))
    );
    
    // Force detection
    console.log('🔄 Forcing customization detection...');
    this.detectCustomizations();
  }
}

/**
 * Color Management Module
 */
class ColorManager {
  constructor(configurator) {
    this.configurator = configurator;
  }
  
  createDynamicSections(detectedOptions) {
    const configGroup = this.configurator.root.querySelector('#color-loading').closest('.config-group');
    const loadingState = this.configurator.root.querySelector('#color-loading');
    
    if (!configGroup) return;
    
    // Remove loading state
    if (loadingState) {
      loadingState.remove();
    }
    
    // Separate ring size options from color options
    const ringSizeOptions = detectedOptions.filter(option => {
      const sectionKey = this.generateSectionKey(option.title);
      return sectionKey === 'ring-sizr' || sectionKey === 'ring-size' || option.title.toLowerCase().includes('ring size');
    });
    
    const colorOptions = detectedOptions.filter(option => {
      const sectionKey = this.generateSectionKey(option.title);
      return !(sectionKey === 'ring-sizr' || sectionKey === 'ring-size' || option.title.toLowerCase().includes('ring size'));
    });
    
    // Create ring size section first (above colors)
    if (ringSizeOptions.length > 0) {
      this.createRingSizeSection(ringSizeOptions[0], configGroup);
    }
    
    // Create color sections
    colorOptions.forEach((option, index) => {
      const sectionKey = this.generateSectionKey(option.title);
      const selectedColor = option.colors.find(c => c.selected) || option.colors[0];
      
      // Update configurator state
      this.configurator.config[sectionKey] = selectedColor.name.toLowerCase().replace(/\s+/g, '-');
      this.configurator.dynamicOptions[sectionKey] = {
        title: option.title,
        colors: option.colors,
        selectedColor: selectedColor
      };
      
      const sectionHTML = this.createSectionHTML(option, sectionKey, index);
      configGroup.insertAdjacentHTML('beforeend', sectionHTML);
      
      // Add event listeners
      const section = configGroup.querySelector(`[data-section-key="${sectionKey}"]`);
      this.attachSectionListeners(section, sectionKey);
      
      // Force apply texture backgrounds after DOM creation
      this.forceApplyTextureBackgrounds(section);
      
      // Animate in
      setTimeout(() => {
        section.classList.add('loaded');
      }, index * 100 + 200);
    });
    
    console.log(`✅ Created ring size section and ${colorOptions.length} dynamic color sections`);
  }
  
  generateSectionKey(title) {
    return title.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }
  
  createSectionHTML(option, sectionKey, index) {
    const selectedColor = option.colors.find(c => c.selected) || option.colors[0];
    
    const swatchesHTML = option.colors.map(color => {
      const colorName = color.name.toLowerCase().replace(/\s+/g, '-');
      const cssClass = `color-${colorName}`;
      const isMetallic = ['platinum', 'gold'].includes(colorName.toLowerCase());
      const isPremium = isMetallic || colorName.includes('camo');
      const metallicClass = isMetallic ? ' metallic-swatch' : '';
      const premiumClass = isPremium ? ' premium-swatch' : '';
      
      // Handle texture/image backgrounds and custom colors
      let inlineStyle = '';
      let finalCssClass = cssClass;
      
      if (color.isTexture && color.textureUrl) {
        // For texture-based colors (camo, gold, platinum)
        inlineStyle = `style="background: url('${color.textureUrl}') !important; background-size: cover !important; background-position: center !important; background-repeat: no-repeat !important;"`;
        finalCssClass = `color-texture color-${colorName}`;
      } else if (color.name === 'Unknown' || color.name === 'Custom') {
        if (color.computedColor) {
          inlineStyle = `style="background-color: ${color.computedColor};"`;
          finalCssClass = 'color-custom-texture';
        } else {
          finalCssClass = 'color-unknown';
        }
      } else if (color.computedColor && !color.isTexture) {
        // For solid colors detected from gradients
        inlineStyle = `style="background-color: ${color.computedColor};"`;
      }
      
      return `
        <div class="color-swatch ${finalCssClass}${metallicClass}${premiumClass} ${color.selected ? 'selected' : ''}" 
             data-color="${colorName}" 
             data-name="${color.name}" 
             data-tda-id="${color.id}" 
             title="${color.name}"
             ${inlineStyle}>
          ${isPremium ? '<div class="premium-indicator"></div>' : ''}
        </div>
      `;
    }).join('');
    
    return `
      <div class="color-section" data-tda-section="true" data-section-key="${sectionKey}">
        <div class="color-label">
          <span>${option.title}</span>
          <span class="selected-color" id="${sectionKey}-selected">${selectedColor.name}</span>
        </div>
        <div class="color-options" data-option="${sectionKey}">
          ${swatchesHTML}
        </div>
      </div>
    `;
  }
  
  createRingSizeSection(ringSizeOption, insertAfterElement) {
    const sectionKey = this.generateSectionKey(ringSizeOption.title);
    const selectedSize = ringSizeOption.colors.find(c => c.selected) || ringSizeOption.colors[0];
    
    // Update configurator state
    this.configurator.config[sectionKey] = selectedSize.name.toLowerCase().replace(/\s+/g, '-');
    this.configurator.dynamicOptions[sectionKey] = {
      title: ringSizeOption.title,
      colors: ringSizeOption.colors,
      selectedColor: selectedSize
    };
    
    // Get ring size options
    const options = ringSizeOption.colors.map(size => ({
      value: size.name.toLowerCase().replace(/\s+/g, '-'),
      name: size.name,
      tdaId: size.id,
      selected: size.selected
    }));
    
    // Create ring size section HTML
    const ringSizeSectionHTML = `
      <div class="config-group ring-size-config">
        <h3 class="config-group__title">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <path d="M9 9h6v6H9z"></path>
          </svg>
          Ring Size
        </h3>
        <div class="ring-size-selector">
          <select class="ring-size-select tda-ring-size-select" data-section-key="${sectionKey}">
            ${options.map(option => `
              <option value="${option.value}" data-tda-id="${option.tdaId}" ${option.selected ? 'selected' : ''}>
                ${option.name}
              </option>
            `).join('')}
          </select>
        </div>
      </div>
    `;
    
    // Insert ring size section before the colors config group
    insertAfterElement.insertAdjacentHTML('beforebegin', ringSizeSectionHTML);
    
    // Find the newly created section
    const ringSizeSection = this.configurator.root.querySelector('.ring-size-config');
    const select = ringSizeSection.querySelector('.ring-size-select');
    
    if (select) {
      select.addEventListener('change', (e) => {
        const selectedOption = e.target.options[e.target.selectedIndex];
        const tdaId = selectedOption.dataset.tdaId;
        const name = selectedOption.textContent;
        
        // Update config
        this.configurator.config[sectionKey] = selectedOption.value;
        
        // Update dynamic options
        if (this.configurator.dynamicOptions[sectionKey]) {
          this.configurator.dynamicOptions[sectionKey].selectedColor = {
            name: name,
            id: tdaId,
            color: selectedOption.value
          };
        }
        
        // Sync with TDA
        this.configurator.tda.triggerColorChange(tdaId);
        
        // Update summary
        this.configurator.ui.updateSummary();
        
        // Trigger price update after a short delay to allow TDA to process the change
        setTimeout(() => {
          if (this.configurator.ui.updatePriceDisplay) {
            this.configurator.ui.updatePriceDisplay();
          }
        }, 500);
        
        console.log(`📐 Ring size selected: ${sectionKey} = ${selectedOption.value} (${name})`);
      });
    }
    
    // Animate in
    setTimeout(() => {
      ringSizeSection.classList.add('loaded');
    }, 100);
    
    console.log('✅ Ring size section created above colors');
  }
  
  attachSectionListeners(section, sectionKey) {
    const swatches = section.querySelectorAll('.color-swatch');
    swatches.forEach(swatch => {
      swatch.addEventListener('click', () => {
        this.handleColorSelection(swatch, sectionKey);
      });
    });
  }
  
  forceApplyTextureBackgrounds(section) {
    // Force apply texture backgrounds via JavaScript for better control
    const textureSwatches = section.querySelectorAll('.color-swatch[style*="background"]');
    textureSwatches.forEach(swatch => {
      const style = swatch.getAttribute('style');
      if (style && style.includes('background')) {
        // Extract the background URL
        const urlMatch = style.match(/url\(['"]*([^'"]*)['"]*\)/);
        if (urlMatch) {
          const imageUrl = urlMatch[1];
          console.log(`🖼️ Force applying texture: ${imageUrl} to ${swatch.dataset.name}`);
          
          // Clear any existing background
          swatch.style.background = '';
          swatch.style.backgroundColor = '';
          
          // Apply the texture background with high priority
          swatch.style.setProperty('background', `url('${imageUrl}')`, 'important');
          swatch.style.setProperty('background-size', 'cover', 'important');
          swatch.style.setProperty('background-position', 'center', 'important');
          swatch.style.setProperty('background-repeat', 'no-repeat', 'important');
        }
      }
    });
  }
  
  handleColorSelection(swatch, sectionKey) {
    const color = swatch.dataset.color;
    const name = swatch.dataset.name;
    const tdaId = swatch.dataset.tdaId;
    
    // Update UI
    const section = swatch.closest('.color-section');
    section.querySelectorAll('.color-swatch').forEach(s => s.classList.remove('selected'));
    swatch.classList.add('selected');
    
    // Update selected display
    const selectedSpan = section.querySelector(`#${sectionKey}-selected`);
    if (selectedSpan) {
      selectedSpan.textContent = name;
    }
    
    // Update config
    this.configurator.config[sectionKey] = color;
    
    // Update dynamic options
    if (this.configurator.dynamicOptions[sectionKey]) {
      this.configurator.dynamicOptions[sectionKey].selectedColor = {
        name: name,
        id: tdaId,
        color: color
      };
    }
    
    // Sync with TDA
    this.configurator.tda.triggerColorChange(tdaId);
    
    // Update summary
    this.configurator.ui.updateSummary();
    
    // Trigger price update for ring size changes (most important for pricing)
    if (sectionKey === 'ring-size' || sectionKey === 'ring-sizr') {
      setTimeout(() => {
        if (this.configurator.ui.updatePriceDisplay) {
          this.configurator.ui.updatePriceDisplay();
        }
      }, 500);
    }
    
    console.log(`🎨 Color selected: ${sectionKey} = ${color}`);
  }
}

/**
 * UI Management Module
 */
class UIManager {
  constructor(configurator) {
    this.configurator = configurator;
  }
  
  initializeTextField() {
    const textInput = this.configurator.root.querySelector('#custom-text');
    const charCount = this.configurator.root.querySelector('#char-count');
    const textConfig = this.configurator.root.querySelector('#text-config');
    
    // Check if text customization is available
    const tdaTextInputs = document.querySelectorAll('input[id*="tda-customizer-customization-option-value-custom-input-"]');
    if (tdaTextInputs.length === 0) {
      console.log('ℹ️ Text customization not available');
      return;
    }
    
    // Show text config section
    if (textConfig) {
      textConfig.style.display = 'block';
    }
    
    if (!textInput) return;
    
    const handleTextChange = (e) => {
      const value = e.target.value;
      this.configurator.config.customText = value;
      
      if (charCount) {
        charCount.textContent = value.length;
      }
      
      this.configurator.tda.syncText(value);
      this.updateSummary();
    };
    
    textInput.addEventListener('input', handleTextChange);
    textInput.addEventListener('paste', (e) => {
      setTimeout(() => handleTextChange(e), 50);
    });
    
    // Initialize with current value
    handleTextChange({ target: textInput });
    
    console.log('✅ Text field initialized');
  }
  
  setupPriceSync() {
    console.log('🏷️ Setting up price synchronization...');
    
    // Find TDA price element
    const tdaPriceElement = document.querySelector('[id*="tda-customizer-variant-price-"]');
    if (!tdaPriceElement) {
      console.log('⚠️ TDA price element not found yet, will try again later');
      // Retry after TDA loads
      setTimeout(() => {
        this.setupPriceSync();
      }, 2000);
      return;
    }
    
    // Set up MutationObserver to watch for price changes
    this.priceObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          this.updatePriceDisplay();
        }
      });
    });
    
    // Start observing the TDA price element
    this.priceObserver.observe(tdaPriceElement, {
      childList: true,
      subtree: true,
      characterData: true
    });
    
    // Initial price sync
    this.updatePriceDisplay();
    
    console.log('✅ Price synchronization setup complete');
  }
  
  updatePriceDisplay() {
    // Get TDA price
    const tdaPriceElement = document.querySelector('[id*="tda-customizer-variant-price-"]');
    if (!tdaPriceElement) return;
    
    const tdaPrice = tdaPriceElement.textContent.trim();
    
    // Update our custom price display
    const customPriceElement = this.configurator.root.querySelector('.price__current');
    if (customPriceElement && tdaPrice) {
      // Clean up the price format (remove "US$" prefix if present)
      const cleanPrice = tdaPrice.replace(/^US\$/, '$');
      customPriceElement.textContent = cleanPrice;
      console.log(`💰 Price updated: ${cleanPrice}`);
      
      // Trigger a visual update animation
      customPriceElement.style.opacity = '0.5';
      setTimeout(() => {
        customPriceElement.style.opacity = '1';
      }, 150);
    }
    
    // Also check for compare at price
    const tdaCompareElement = document.querySelector('[id*="tda-customizer-variant-compare-at-price-"]');
    const customCompareElement = this.configurator.root.querySelector('.price__compare');
    
    if (tdaCompareElement && customCompareElement) {
      const tdaComparePrice = tdaCompareElement.textContent.trim();
      if (tdaComparePrice) {
        const cleanComparePrice = tdaComparePrice.replace(/^US\$/, '$');
        customCompareElement.textContent = cleanComparePrice;
        customCompareElement.style.display = 'inline';
        
        // Show savings badge if both prices exist
        const priceBadge = this.configurator.root.querySelector('.price__badge');
        if (priceBadge && tdaPrice) {
          // Calculate savings (simplified - you might want more robust price parsing)
          const currentPrice = parseFloat(tdaPrice.replace(/[^0-9.]/g, ''));
          const comparePrice = parseFloat(tdaComparePrice.replace(/[^0-9.]/g, ''));
          if (comparePrice > currentPrice) {
            const savings = comparePrice - currentPrice;
            priceBadge.textContent = `Save $${savings.toFixed(2)}`;
            priceBadge.style.display = 'inline';
          }
        }
      } else {
        customCompareElement.style.display = 'none';
        const priceBadge = this.configurator.root.querySelector('.price__badge');
        if (priceBadge) priceBadge.style.display = 'none';
      }
    }
  }
  
  updateLogoPricingDisplay() {
    // Get pricing based on product type
    const logoPrice = (this.configurator.productType === 'bag' || this.configurator.productType === 'heavy-bag') ? '$65.00' : '$1,850.00';
    const logoPriceShort = (this.configurator.productType === 'bag' || this.configurator.productType === 'heavy-bag') ? '$65' : '$1,850';
    
    // Update the logo price display in toggle
    const logoPriceElement = this.configurator.root.querySelector('#logo-price');
    if (logoPriceElement) {
      logoPriceElement.textContent = `+${logoPrice}`;
    }
    
    // Update the pricing note text
    const logoPricingNote = this.configurator.root.querySelector('#logo-pricing-note');
    if (logoPricingNote) {
      const noteText = logoPricingNote.childNodes[2]; // Text node after the SVG
      if (noteText && noteText.nodeType === Node.TEXT_NODE) {
        noteText.textContent = `Custom logo setup and printing adds ${logoPriceShort} to your order`;
      }
    }
    
    console.log(`🔄 Updated logo pricing display: +${logoPrice} for ${this.configurator.productType}`);
  }
  
  setupLogoUpload() {
    const uploadZone = this.configurator.root.querySelector('#upload-zone');
    const logoInput = this.configurator.root.querySelector('#logo-input');
    const logoPreview = this.configurator.root.querySelector('#logo-preview');
    const logoPreviewImg = this.configurator.root.querySelector('#logo-preview-img');
    const logoStatus = this.configurator.root.querySelector('#logo-status');
    const logoEdit = this.configurator.root.querySelector('#logo-edit');
    const logoRemove = this.configurator.root.querySelector('#logo-remove');
    const logoEnabled = this.configurator.root.querySelector('#logo-enabled');
    const logoUploadContainer = this.configurator.root.querySelector('#logo-upload');
    const logoUploadTip = this.configurator.root.querySelector('#logo-upload-tip');
    
    if (!uploadZone || !logoInput || !logoEnabled) return;
    
    // Update logo pricing display based on product type
    this.updateLogoPricingDisplay();
    
    // Logo checkbox event listener
    logoEnabled.addEventListener('change', (e) => {
      this.handleLogoToggle(e.target.checked);
    });
    
    // Initialize with logo disabled
    this.handleLogoToggle(false);
    
    // Click to upload
    uploadZone.addEventListener('click', () => logoInput.click());
    
    // File input change
    logoInput.addEventListener('change', (e) => {
      if (e.target.files?.[0]) {
        this.handleLogoUpload(e.target.files[0]);
      }
    });
    
    // Drag and drop
    uploadZone.addEventListener('dragover', (e) => {
      e.preventDefault();
      uploadZone.classList.add('dragover');
    });
    
    uploadZone.addEventListener('dragleave', () => {
      uploadZone.classList.remove('dragover');
    });
    
    uploadZone.addEventListener('drop', (e) => {
      e.preventDefault();
      uploadZone.classList.remove('dragover');
      if (e.dataTransfer.files?.[0]) {
        this.handleLogoUpload(e.dataTransfer.files[0]);
      }
    });
    
    // Edit and remove buttons
    if (logoEdit) {
      logoEdit.addEventListener('click', () => {
        if (this.configurator.config.customLogo?.file) {
          // Use original file if available, otherwise current file
          const fileToEdit = this.configurator.config.customLogo.originalFile || this.configurator.config.customLogo.file;
          this.configurator.logoEditor.open(fileToEdit, true);
        }
      });
    }
    
    if (logoRemove) {
      logoRemove.addEventListener('click', () => this.removeLogo());
    }
    
    console.log('✅ Logo upload initialized');
  }
  
  handleLogoToggle(enabled) {
    const logoStatus = this.configurator.root.querySelector('#logo-status');
    const logoUploadContainer = this.configurator.root.querySelector('#logo-upload');
    const logoUploadTip = this.configurator.root.querySelector('#logo-upload-tip');
    const logoPreview = this.configurator.root.querySelector('#logo-preview');
    const logoPricingNote = this.configurator.root.querySelector('#logo-pricing-note');
    
    // Get pricing based on product type
    const logoPrice = (this.configurator.productType === 'bag' || this.configurator.productType === 'heavy-bag') ? '$65' : '$1,850';
    
    console.log(`🔄 Logo toggle: ${enabled ? 'ENABLING' : 'DISABLING'} logo (+${logoPrice})`);
    
    if (enabled) {
      // Enable logo upload
      if (logoStatus) logoStatus.textContent = 'None';
      if (logoUploadContainer) logoUploadContainer.style.display = 'block';
      if (logoUploadTip) logoUploadTip.style.display = 'block';
      
      // Show pricing note with correct price
      if (logoPricingNote) {
        logoPricingNote.style.display = 'flex';
        // Update pricing note text if it exists
        const priceText = logoPricingNote.querySelector('.logo-price');
        if (priceText) {
          priceText.textContent = logoPrice;
        }
      }
      
      // Sync with TDA - use new unified method
      const success = this.syncTDALogoOption(true);
      
      if (success) {
        console.log(`✅ Logo upload enabled (+${logoPrice}) - TDA synced`);
      } else {
        console.warn('⚠️ Logo enabled but TDA sync failed - retrying...');
        // Retry after a delay
        setTimeout(() => {
          this.syncTDALogoOption(true);
        }, 1500);
      }
      
    } else {
      // Disable logo upload and remove any existing logo
      if (logoStatus) logoStatus.textContent = 'Disabled';
      if (logoUploadContainer) logoUploadContainer.style.display = 'none';
      if (logoUploadTip) logoUploadTip.style.display = 'none';
      
      // Hide pricing note
      if (logoPricingNote) {
        logoPricingNote.style.display = 'none';
      }
      
      // Hide preview if shown
      if (logoPreview) {
        logoPreview.style.display = 'none';
      }
      
      // Clear logo from config
      this.configurator.config.customLogo = null;
      
      // Sync with TDA - use new unified method
      const success = this.syncTDALogoOption(false);
      
      if (success) {
        console.log('✅ Logo upload disabled - TDA synced');
      } else {
        console.warn('⚠️ Logo disabled but TDA sync failed - retrying...');
        // Retry after a delay
        setTimeout(() => {
          this.syncTDALogoOption(false);
        }, 1500);
      }
    }
    
    // Update summary after a short delay to allow TDA to process
    setTimeout(() => {
      this.updateSummary();
    }, 300);
    
    // Force price update after TDA has had time to process
    setTimeout(() => {
      if (this.updatePriceDisplay) {
        this.updatePriceDisplay();
        console.log('💰 Price display updated after logo toggle');
      }
    }, 1000);
  }
  
  handleLogoUpload(file) {
    // Validate file
    if (!file.type.match('image/(jpeg|jpg|png)')) {
      alert('Please upload a JPG or PNG file');
      return;
    }
    
    if (file.size > 4 * 1024 * 1024) {
      alert('File too large. Maximum size is 4MB');
      return;
    }
    
    console.log('📁 Handling logo upload:', file.name);
    
    // Open editor for processing
    this.configurator.logoEditor.open(file);
  }
  
  updateLogoPreview(file) {
    const uploadZone = this.configurator.root.querySelector('#upload-zone');
    const logoPreview = this.configurator.root.querySelector('#logo-preview');
    const logoPreviewImg = this.configurator.root.querySelector('#logo-preview-img');
    const logoStatus = this.configurator.root.querySelector('#logo-status');
    
    if (!logoPreview || !logoPreviewImg) return;
    
    // Clean up previous URL
    if (logoPreviewImg.src?.startsWith('blob:')) {
      URL.revokeObjectURL(logoPreviewImg.src);
    }
    
    // Create new preview
    const previewUrl = URL.createObjectURL(file);
    logoPreviewImg.src = previewUrl;
    
    // Update UI
    if (uploadZone) uploadZone.parentElement.style.display = 'none';
    logoPreview.style.display = 'flex';
    
    if (logoStatus) {
      logoStatus.textContent = file.name.length > 15 ? 
        file.name.substring(0, 15) + '...' : file.name;
    }
    
    // Update config
    this.configurator.config.customLogo = {
      name: file.name,
      size: file.size,
      type: file.type,
      file: file,
      originalFile: file, // Keep reference to original for consistent scaling
      previewUrl: previewUrl,
      transformState: null // Will be set when logo is edited
    };
    
    // Sync with TDA
    this.configurator.tda.syncLogo(file);
    
    // Update summary
    this.updateSummary();
    
    console.log('✅ Logo preview updated:', file.name);
  }
  
  removeLogo() {
    const uploadZone = this.configurator.root.querySelector('#upload-zone');
    const logoPreview = this.configurator.root.querySelector('#logo-preview');
    const logoPreviewImg = this.configurator.root.querySelector('#logo-preview-img');
    const logoStatus = this.configurator.root.querySelector('#logo-status');
    const logoInput = this.configurator.root.querySelector('#logo-input');
    
    // Clean up URLs
    if (this.configurator.config.customLogo?.previewUrl) {
      URL.revokeObjectURL(this.configurator.config.customLogo.previewUrl);
    }
    if (logoPreviewImg?.src?.startsWith('blob:')) {
      URL.revokeObjectURL(logoPreviewImg.src);
    }
    
    // Reset UI
    if (uploadZone) uploadZone.parentElement.style.display = 'block';
    if (logoPreview) logoPreview.style.display = 'none';
    if (logoPreviewImg) logoPreviewImg.src = '';
    if (logoInput) logoInput.value = '';
    if (logoStatus) logoStatus.textContent = 'None';
    
    // Update config
    this.configurator.config.customLogo = null;
    
    // Clear logo from TDA viewer
    this.configurator.tda.clearLogo();
    
    // Update summary
    this.updateSummary();
    
    console.log('✅ Logo removed');
  }
  
  syncTDALogoOption(enabled) {
    const productType = this.configurator.productType;
    console.log(`🔄 Attempting to sync TDA logo option for ${productType.toUpperCase()}: ${enabled ? 'ENABLED' : 'DISABLED'}`);
    
    let success = false;
    
    if (productType === 'bag' || productType === 'heavy-bag') {
      // For bags: handle file upload approach
      success = this.syncBagLogoOption(enabled);
    } else {
      // For rings: handle dropdown approach
      success = this.syncRingLogoOption(enabled);
    }
    
    if (success) {
      // Trigger price update after a delay to allow TDA to process
      setTimeout(() => {
        this.updatePriceDisplay();
        console.log('💰 Price update triggered after logo option change');
      }, 800);
    }
    
    return success;
  }
  
  syncRingLogoOption(enabled) {
    const value = enabled ? '8wvsxv' : '0zxrjp'; // CUSTOM LOGO : NO LOGO
    
    // Try multiple selectors for TDA logo dropdown
    const selectors = [
      '[id*="tda-customizer-customization-option-dropdown-"]',
      'select[id*="tda-customizer-customization-option-dropdown-"]',
      '.tda-customizer-customization-option select',
      'select[id*="tda-"][id*="dropdown"]'
    ];
    
    let tdaDropdown = null;
    for (const selector of selectors) {
      tdaDropdown = document.querySelector(selector);
      if (tdaDropdown) {
        console.log(`✅ Found TDA dropdown with selector: ${selector}`);
        break;
      }
    }
    
    // If still not found, try to find by logo-related content
    if (!tdaDropdown) {
      const allDropdowns = document.querySelectorAll('select[id*="tda-"]');
      console.log(`🔍 Checking ${allDropdowns.length} TDA dropdowns for logo options...`);
      
      for (const dropdown of allDropdowns) {
        const options = Array.from(dropdown.options);
        const hasLogoOptions = options.some(option => 
          option.textContent.toLowerCase().includes('logo') ||
          option.value === '8wvsxv' ||
          option.value === '0zxrjp'
        );
        
        if (hasLogoOptions) {
          tdaDropdown = dropdown;
          console.log(`✅ Found logo dropdown by content: ${dropdown.id}`);
          console.log('Available options:', options.map(opt => `${opt.value}: ${opt.textContent}`));
          break;
        }
      }
    }
    
    if (tdaDropdown) {
      const previousValue = tdaDropdown.value;
      tdaDropdown.value = value;
      
      // Trigger multiple events to ensure TDA responds
      ['change', 'input'].forEach(eventType => {
        const event = new Event(eventType, { bubbles: true, cancelable: true });
        tdaDropdown.dispatchEvent(event);
      });
      
      console.log(`🔄 TDA ring logo option synced: ${previousValue} -> ${value}`);
      return true;
    } else {
      console.warn('⚠️ TDA ring logo dropdown not found');
      return false;
    }
  }
  
  syncBagLogoOption(enabled) {
    // For bags, find the logo dropdown by looking for the specific dropdown pattern
    const logoDropdown = document.querySelector('select[id*="tda-customizer-customization-option-dropdown-"]');
    
    if (!logoDropdown) {
      console.warn('⚠️ TDA bag logo dropdown not found');
      return false;
    }
    
    console.log(`✅ Found bag logo dropdown: ${logoDropdown.id}`);
    console.log('Available options:', Array.from(logoDropdown.options).map(opt => `${opt.value}: ${opt.textContent}`));
    
    // Find the parent container for file upload
    const logoContainer = logoDropdown.closest('[id*="tda-customizer-customization-option-"]') || 
                         logoDropdown.closest('.tda-customizer-customization-option');
    
    if (enabled) {
      // Enable logo - select the "CUSTOM LOGO" option
      // Look for option that contains "CUSTOM LOGO" in text content
      let customLogoOption = null;
      for (const option of logoDropdown.options) {
        if (option.textContent.toUpperCase().includes('CUSTOM LOGO')) {
          customLogoOption = option;
          break;
        }
      }
      
      if (!customLogoOption) {
        console.warn('⚠️ Custom logo option not found in dropdown');
        return false;
      }
      
      const previousValue = logoDropdown.value;
      logoDropdown.value = customLogoOption.value;
      
      // Trigger events to notify TDA of the change
      ['change', 'input'].forEach(eventType => {
        const event = new Event(eventType, { bubbles: true, cancelable: true });
        logoDropdown.dispatchEvent(event);
      });
      
      // After selecting the logo option, ensure the file upload container becomes visible
      setTimeout(() => {
        if (logoContainer) {
          const fileUploadContainer = logoContainer.querySelector('[id*="tda-file-upload-container-"]');
          if (fileUploadContainer) {
            fileUploadContainer.style.display = '';
            console.log('👁️ Made file upload container visible');
          }
        }
      }, 300);
      
      console.log(`🔄 TDA bag logo enabled: ${previousValue} -> ${customLogoOption.value} (${customLogoOption.textContent})`);
      return true;
      
    } else {
      // Disable logo - select the "NO LOGO" option
      let noLogoOption = null;
      for (const option of logoDropdown.options) {
        if (option.textContent.toUpperCase().includes('NO LOGO')) {
          noLogoOption = option;
          break;
        }
      }
      
      if (!noLogoOption) {
        console.warn('⚠️ No logo option not found in dropdown');
        return false;
      }
      
      const previousValue = logoDropdown.value;
      logoDropdown.value = noLogoOption.value;
      
      // Trigger events to notify TDA of the change
      ['change', 'input'].forEach(eventType => {
        const event = new Event(eventType, { bubbles: true, cancelable: true });
        logoDropdown.dispatchEvent(event);
      });
      
      // Clear any file input
      if (logoContainer) {
        const fileInput = logoContainer.querySelector('input[type="file"]');
        if (fileInput) {
          fileInput.value = '';
          fileInput.dispatchEvent(new Event('change', { bubbles: true }));
          console.log('🔄 Cleared file input');
        }
      }
      
      console.log(`🔄 TDA bag logo disabled: ${previousValue} -> ${noLogoOption.value} (${noLogoOption.textContent})`);
      return true;
    }
  }

  updateSummary() {
    const configList = this.configurator.root.querySelector('#config-list');
    if (!configList) return;
    
    let html = '';
    
    // Add ring size from separate section
    const ringSizeSection = this.configurator.root.querySelector('.ring-size-config .tda-ring-size-select');
    if (ringSizeSection) {
      const selectedOption = ringSizeSection.options[ringSizeSection.selectedIndex];
      if (selectedOption) {
        html += `<div>Ring Size: ${selectedOption.textContent}</div>`;
      }
    }
    
    // Add TDA color options (excluding ring size which is now separate)
    const tdaSections = this.configurator.root.querySelectorAll('.color-section[data-tda-section="true"]');
    tdaSections.forEach(section => {
      const sectionKey = section.getAttribute('data-section-key');
      // Skip ring size sections since they're handled separately now
      if ((sectionKey === 'ring-sizr' || sectionKey === 'ring-size' || sectionKey.includes('ring-size'))) {
        return;
      }
      
      if (this.configurator.config[sectionKey] && this.configurator.dynamicOptions[sectionKey]) {
        const title = this.configurator.dynamicOptions[sectionKey].title;
        const colorValue = this.configurator.config[sectionKey];
        const colorName = colorValue.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        html += `<div>${title}: ${colorName}</div>`;
      }
    });
    
    // Add text if available
    const textConfig = this.configurator.root.querySelector('#text-config');
    if (textConfig && textConfig.style.display !== 'none' && this.configurator.config.customText) {
      html += `<div>Text: "${this.configurator.config.customText}"</div>`;
    }
    
    // Add logo with correct pricing
    const logoEnabled = this.configurator.root.querySelector('#logo-enabled');
    const logoPrice = (this.configurator.productType === 'bag' || this.configurator.productType === 'heavy-bag') ? '$65' : '$1,850';
    
    if (logoEnabled && logoEnabled.checked) {
      if (this.configurator.config.customLogo) {
        const logoName = this.configurator.config.customLogo.name.length > 20 ? 
          this.configurator.config.customLogo.name.substring(0, 20) + '...' : 
          this.configurator.config.customLogo.name;
        html += `<div>Logo: ${logoName} (+${logoPrice})</div>`;
      } else {
        html += `<div>Logo: No logo uploaded (+${logoPrice})</div>`;
      }
    } else {
      html += `<div>Logo: Disabled</div>`;
    }
    
    configList.innerHTML = html;
  }
  
  testTextField() {
    const textInput = this.configurator.root.querySelector('#custom-text');
    const charCount = this.configurator.root.querySelector('#char-count');
    
    console.log('🧪 Testing text field:', {
      input: !!textInput,
      charCount: !!charCount,
      currentValue: textInput?.value,
      configValue: this.configurator.config.customText
    });
    
    return !!textInput && !!charCount;
  }
  
  testLogoUpload() {
    const uploadZone = this.configurator.root.querySelector('#upload-zone');
    const logoInput = this.configurator.root.querySelector('#logo-input');
    const logoPreview = this.configurator.root.querySelector('#logo-preview');
    
    console.log('🧪 Testing logo upload:', {
      uploadZone: !!uploadZone,
      logoInput: !!logoInput,
      logoPreview: !!logoPreview,
      currentLogo: this.configurator.config.customLogo
    });
    
    return !!uploadZone && !!logoInput && !!logoPreview;
  }
}

/**
 * Logo Editor Module
 */
class LogoEditor {
  constructor(configurator) {
    this.configurator = configurator;
    this.modal = document.querySelector('#logo-modal');
    this.canvas = document.querySelector('#logo-canvas');
    this.ctx = this.canvas?.getContext('2d');
    this.originalFile = null; // Always keep original file
    this.currentFile = null;
    this.originalImage = null;
    this.state = {
      rotation: 0,
      scale: this.getDefaultScale(), // Product-specific default scale
      x: 0,
      y: 0,
      verticalPosition: 0, // New vertical position control (-50 to 50)
      flipH: false
    };
    
    this.setupEventListeners();
  }
  
  getDefaultScale() {
    // Updated defaults: 80% for bags, 65% for rings
    return (this.configurator.productType === 'bag' || this.configurator.productType === 'heavy-bag') ? 0.8 : 0.65;
  }
  
  setupEventListeners() {
    if (!this.modal) return;
    
    const modalClose = this.modal.querySelector('#modal-close');
    const modalCancel = this.modal.querySelector('#modal-cancel');
    const modalApply = this.modal.querySelector('#modal-apply');
    const overlay = this.modal.querySelector('.modal__overlay');
    
    // Close modal
    [modalClose, modalCancel, overlay].forEach(el => {
      if (el) {
        el.addEventListener('click', () => this.close());
      }
    });
    
    // Controls
    const rotateSlider = this.modal.querySelector('#rotate-slider');
    const scaleSlider = this.modal.querySelector('#scale-slider');
    const positionSlider = this.modal.querySelector('#position-slider');
    const presetVertical = this.modal.querySelector('#preset-vertical');
    const presetHorizontal = this.modal.querySelector('#preset-horizontal');
    const rotateLeft = this.modal.querySelector('#rotate-left');
    const rotateRight = this.modal.querySelector('#rotate-right');
    const flipH = this.modal.querySelector('#flip-h');
    const resetAll = this.modal.querySelector('#reset-all');
    
    if (rotateSlider) {
      rotateSlider.addEventListener('input', (e) => {
        this.state.rotation = parseFloat(e.target.value);
        this.updateRotationDisplay();
        this.render();
      });
    }
    
    if (scaleSlider) {
      scaleSlider.addEventListener('input', (e) => {
        this.state.scale = parseFloat(e.target.value);
        this.updateScaleDisplay();
        this.render();
      });
    }
    
    if (positionSlider) {
      positionSlider.addEventListener('input', (e) => {
        this.state.verticalPosition = parseFloat(e.target.value);
        this.updatePositionDisplay();
        this.render();
      });
    }
    
    if (presetVertical) {
      presetVertical.addEventListener('click', () => this.applyPreset('vertical'));
    }
    
    if (presetHorizontal) {
      presetHorizontal.addEventListener('click', () => this.applyPreset('horizontal'));
    }
    
    if (rotateLeft) {
      rotateLeft.addEventListener('click', () => this.rotate(-90));
    }
    
    if (rotateRight) {
      rotateRight.addEventListener('click', () => this.rotate(90));
    }
    
    if (flipH) {
      flipH.addEventListener('click', () => this.flip());
    }
    
    if (resetAll) {
      resetAll.addEventListener('click', () => this.reset());
    }
    
    // Apply changes
    if (modalApply) {
      modalApply.addEventListener('click', () => this.apply());
    }
    
    // Canvas dragging
    if (this.canvas) {
      let isDragging = false;
      let lastX, lastY;
      
      this.canvas.addEventListener('mousedown', (e) => {
        isDragging = true;
        const rect = this.canvas.getBoundingClientRect();
        lastX = e.clientX - rect.left;
        lastY = e.clientY - rect.top;
      });
      
      this.canvas.addEventListener('mousemove', (e) => {
        if (!isDragging) return;
        
        const rect = this.canvas.getBoundingClientRect();
        const currentX = e.clientX - rect.left;
        const currentY = e.clientY - rect.top;
        
        // Apply position changes with scale compensation
        const deltaX = (currentX - lastX) / this.state.scale;
        const deltaY = (currentY - lastY) / this.state.scale;
        this.state.x += deltaX;
        this.state.y += deltaY;
        
        lastX = currentX;
        lastY = currentY;
        
        this.render();
      });
      
      this.canvas.addEventListener('mouseup', () => {
        isDragging = false;
      });
    }
  }
  
  open(file, preserveState = false) {
    if (!this.modal || !this.canvas) {
      console.error('❌ Logo editor not available');
      return;
    }
    
    // For new files, set both current and original
    // For re-editing, always use the original file for editing
    if (!preserveState || !this.originalFile) {
      this.originalFile = file;
      this.currentFile = file;
    } else {
      // Always edit from original file to avoid cumulative scaling issues
      this.currentFile = this.originalFile;
    }
    
    // Reset to defaults for new files, preserve state when re-editing
    if (!preserveState) {
      this.reset();
    }
    
    // Load image from the current file
    const img = new Image();
    img.onload = () => {
      this.originalImage = img;
      this.setupCanvas();
      
      // Restore saved transform state if preserving state
      if (preserveState && this.configurator.config.customLogo?.transformState) {
        this.state = { ...this.configurator.config.customLogo.transformState };
        console.log('✅ Restored logo transform state:', this.state);
      } else {
        // Set product-specific default scale for new logos
        this.state.scale = this.getDefaultScale();
      }
      
      this.updateControls();
      this.updateSliderDefaults(); // Update slider to match product type
      this.render();
      this.modal.classList.add('active');
      document.body.style.overflow = 'hidden';
    };
    img.onerror = () => {
      console.error('❌ Failed to load image for logo editor');
      alert('Failed to load image. Please try again.');
    };
    img.src = URL.createObjectURL(this.currentFile);
  }
  
  close() {
    if (this.modal) {
      this.modal.classList.remove('active');
      document.body.style.overflow = '';
    }
    
    // Clean up
    if (this.originalImage?.src?.startsWith('blob:')) {
      URL.revokeObjectURL(this.originalImage.src);
    }
    
    console.log('❌ Logo editor closed');
  }
  
  setupCanvas() {
    if (!this.canvas || !this.originalImage) return;
    
    // Set canvas to fixed 300x300 pixels
    const size = 300;
    
    this.canvas.width = size;
    this.canvas.height = size;
    this.canvas.style.width = size + 'px';
    this.canvas.style.height = size + 'px';
    
    console.log(`📐 Canvas setup: ${size}x${size}px`);
  }
  
  render() {
    if (!this.ctx || !this.originalImage) return;
    
    const canvasSize = this.canvas.width;
    
    // Clear canvas
    this.ctx.clearRect(0, 0, canvasSize, canvasSize);
    
    // Background
    this.ctx.fillStyle = '#f9fafb';
    this.ctx.fillRect(0, 0, canvasSize, canvasSize);
    
    // Border
    this.ctx.strokeStyle = '#e5e7eb';
    this.ctx.lineWidth = 2;
    this.ctx.strokeRect(10, 10, canvasSize - 20, canvasSize - 20);
    
    // Save context
    this.ctx.save();
    
    // Move to center with vertical position offset applied in screen coordinates
    const centerX = canvasSize / 2;
    const centerY = canvasSize / 2;
    const verticalOffset = this.state.verticalPosition * 2; // Scale the position value
    // Apply vertical offset in screen coordinates (before rotation)
    this.ctx.translate(centerX + this.state.x, centerY + this.state.y);
    // Apply screen-space vertical offset
    this.ctx.translate(0, verticalOffset);
    
    // Apply transformations
    if (this.state.rotation !== 0) {
      this.ctx.rotate((this.state.rotation * Math.PI) / 180);
    }
    
    // Apply flip only (scale is handled in image dimensions)
    const scaleX = this.state.flipH ? -1 : 1;
    const scaleY = 1;
    
    if (scaleX !== 1) {
      this.ctx.scale(scaleX, scaleY);
    }
    
    // Use original image dimensions scaled to fit canvas, then apply user scale
    const originalWidth = this.originalImage.naturalWidth;
    const originalHeight = this.originalImage.naturalHeight;
    const maxCanvasSize = canvasSize * 0.8; // Max space in canvas
    
    // Scale original to fit in canvas preview area
    const scale = Math.min(maxCanvasSize / originalWidth, maxCanvasSize / originalHeight);
    const previewBaseWidth = originalWidth * scale;
    const previewBaseHeight = originalHeight * scale;
    
    // Apply user's scale factor
    const imgWidth = previewBaseWidth * this.state.scale;
    const imgHeight = previewBaseHeight * this.state.scale;
    
    this.ctx.drawImage(
      this.originalImage,
      -imgWidth / 2,
      -imgHeight / 2,
      imgWidth,
      imgHeight
    );
    
    // Restore context
    this.ctx.restore();
  }
  
  applyPreset(type) {
    // Reset position and basic transformations
    this.state.x = 0;
    this.state.y = 0;
    this.state.verticalPosition = 0;
    this.state.rotation = 0;
    this.state.scale = this.getDefaultScale();
    
    // Product-specific preset scaling
    const isRing = this.configurator.productType === 'ring';
    const largerScale = isRing ? 1.0 : 0.6; // Rings can go larger, bags stay smaller
    
    if (type === 'vertical') {
      this.state.rotation = 0;
      this.state.scale = largerScale;
    } else if (type === 'horizontal') {
      this.state.rotation = -90;
      this.state.scale = largerScale;
    }
    
    this.updateControls();
    this.render();
    
    // Update preset buttons
    this.modal.querySelectorAll('.preset-btn').forEach(btn => {
      btn.classList.remove('active');
    });
    this.modal.querySelector(`#preset-${type}`)?.classList.add('active');
    
    console.log(`✅ Applied ${type} preset for ${this.configurator.productType}`);
  }
  
  rotate(degrees) {
    this.state.rotation += degrees;
    this.state.rotation = ((this.state.rotation % 360) + 360) % 360;
    if (this.state.rotation > 180) this.state.rotation -= 360;
    
    this.updateControls();
    this.render();
  }
  
  flip() {
    this.state.flipH = !this.state.flipH;
    this.render();
  }
  
  reset() {
    this.state = {
      rotation: 0,
      scale: this.getDefaultScale(), // Product-specific default scale
      x: 0,
      y: 0,
      verticalPosition: 0,
      flipH: false
    };
    
    this.updateControls();
    this.render();
  }
  
  updateControls() {
    const rotateSlider = this.modal.querySelector('#rotate-slider');
    const scaleSlider = this.modal.querySelector('#scale-slider');
    const positionSlider = this.modal.querySelector('#position-slider');
    
    if (rotateSlider) {
      rotateSlider.value = this.state.rotation;
    }
    
    if (scaleSlider) {
      scaleSlider.value = this.state.scale;
    }
    
    if (positionSlider) {
      positionSlider.value = this.state.verticalPosition;
    }
    
    this.updateRotationDisplay();
    this.updateScaleDisplay();
    this.updatePositionDisplay();
  }
  
  updateRotationDisplay() {
    const display = this.modal.querySelector('#rotate-value');
    if (display) {
      display.textContent = `${this.state.rotation}°`;
    }
  }
  
  updateScaleDisplay() {
    const display = this.modal.querySelector('#scale-value');
    if (display) {
      display.textContent = `${Math.round(this.state.scale * 100)}%`;
    }
  }
  
  updatePositionDisplay() {
    const display = this.modal.querySelector('#position-value');
    if (display) {
      const value = this.state.verticalPosition;
      if (value === 0) {
        display.textContent = 'Center';
      } else if (value > 0) {
        display.textContent = `Down ${value}`;
      } else {
        display.textContent = `Up ${Math.abs(value)}`;
      }
    }
  }
  
  updateSliderDefaults() {
    // Update slider min value to allow 10% minimum
    const scaleSlider = this.modal.querySelector('#scale-slider');
    if (scaleSlider) {
      scaleSlider.min = '0.1'; // Allow scaling down to 10%
      scaleSlider.value = this.state.scale;
      console.log(`🎛️ Updated slider for ${this.configurator.productType}: min=10%, default=${Math.round(this.state.scale * 100)}%`);
    }
  }
  
  async apply() {
    try {
      console.log('✅ Applying logo changes...');
      
      // Simple approach: Create a square canvas and apply transformations directly
      const maxSize = 1024; // Standard size for logos
      const outputCanvas = document.createElement('canvas');
      const outputCtx = outputCanvas.getContext('2d');
      
      outputCanvas.width = maxSize;
      outputCanvas.height = maxSize;
      
      console.log(`📐 Output canvas: ${maxSize}x${maxSize}`);
      
      // Clear canvas
      outputCtx.clearRect(0, 0, maxSize, maxSize);
      
      // Save context for transformations
      outputCtx.save();
      
      // Move to center of canvas with vertical offset in screen coordinates
      const verticalOffset = this.state.verticalPosition * 4; // Scale the vertical position for output
      outputCtx.translate(maxSize / 2, maxSize / 2 + verticalOffset);
      
      // Apply rotation
      if (this.state.rotation !== 0) {
        outputCtx.rotate((this.state.rotation * Math.PI) / 180);
      }
      
      // Apply flip
      const scaleX = this.state.flipH ? -1 : 1;
      if (scaleX !== 1) {
        outputCtx.scale(scaleX, 1);
      }
      
      // Calculate image size - fit original image in canvas with user scale
      const originalWidth = this.originalImage.naturalWidth;
      const originalHeight = this.originalImage.naturalHeight;
      
      // Fit image in 80% of canvas, then apply user scale
      const maxFitSize = maxSize * 0.8;
      const fitScale = Math.min(maxFitSize / originalWidth, maxFitSize / originalHeight);
      
      const finalWidth = originalWidth * fitScale * this.state.scale;
      const finalHeight = originalHeight * fitScale * this.state.scale;
      
      console.log(`🖼️ Drawing image: ${finalWidth.toFixed(0)}x${finalHeight.toFixed(0)} (scale: ${this.state.scale}x)`);
      
      // Draw image centered with position offset
      outputCtx.drawImage(
        this.originalImage,
        -finalWidth / 2 + this.state.x * 2, // Scale position for output
        -finalHeight / 2 + this.state.y * 2, // Vertical offset now handled in translate
        finalWidth,
        finalHeight
      );
      
      outputCtx.restore();
      
      // Convert to blob
      const blob = await new Promise(resolve => {
        outputCanvas.toBlob(resolve, 'image/png', 0.95);
      });
      
      // Create new file
      const timestamp = Date.now();
      const editedFile = new File([blob], `logo_${timestamp}.png`, {
        type: 'image/png'
      });
      
      // Save current state for re-editing
      const currentState = { ...this.state };
      
      // Update config with new file and save the original file reference
      if (this.configurator.config.customLogo) {
        this.configurator.config.customLogo.file = editedFile;
        this.configurator.config.customLogo.originalFile = this.originalFile; // Keep original
        this.configurator.config.customLogo.transformState = currentState;
      }
      
      // Update UI
      this.configurator.ui.updateLogoPreview(editedFile);
      
      console.log('✅ Logo transformations applied - original file preserved');
      
      // Close modal
      this.close();
      
      console.log('✅ Logo editing complete');
      
    } catch (error) {
      console.error('❌ Error applying logo changes:', error);
      alert('Error applying changes. Please try again.');
    }
  }
  
  test() {
    console.log('🧪 Testing logo editor:', {
      modal: !!this.modal,
      canvas: !!this.canvas,
      ctx: !!this.ctx,
      currentFile: !!this.currentFile,
      originalImage: !!this.originalImage
    });
    
    return !!this.modal && !!this.canvas && !!this.ctx;
  }
}

// Export for global access
window.BoxingRingConfigurator = BoxingRingConfigurator;

// Debug helper function
window.debugConfigurator = function() {
  console.log('🧪 Manual configurator debug...');
  
  // Find existing configurator instance
  const existingInstance = window.configuratorInstance;
  if (existingInstance) {
    existingInstance.tda.debug();
    return existingInstance;
  }
  
  // Try to create new instance
  const sectionElement = document.querySelector('.ring-configurator');
  if (sectionElement) {
    const sectionId = sectionElement.dataset.sectionId;
    if (sectionId) {
      console.log('🔄 Creating new configurator instance...');
      window.configuratorInstance = new BoxingRingConfigurator(sectionId);
      return window.configuratorInstance;
    }
  }
  
  console.error('❌ Cannot find configurator section');
  return null;
};

// Force detection helper
window.forceDetectColors = function() {
  if (window.configuratorInstance) {
    window.configuratorInstance.tda.detectCustomizations();
  } else {
    console.error('❌ No configurator instance found');
  }
};

// Test aspect ratio helper function
window.testLogoAspectRatios = function() {
  console.log('🔍 Testing logo aspect ratios...');
  
  const existingInstance = window.configuratorInstance;
  if (existingInstance && existingInstance.logoEditor) {
    return existingInstance.logoEditor.testAspectRatios();
  }
  
  console.log('❌ No configurator or logo editor instance found');
  return null;
};

// Debug 3D viewer helper
window.debug3DViewer = function() {
  console.log('🎯 Debugging 3D Viewer...');
  
  const viewer = document.querySelector('[id*="tda-app-section-model-viewer-"]');
  const container = document.querySelector('[id*="tda-app-section-model-viewer-container-"]');
  const stickyContainer = document.querySelector('[id*="tda-app-section-model-viewer-sticky-container-"]');
  const placeholder = document.querySelector('#viewer-placeholder');
  
  console.log('3D Viewer Elements:', {
    viewer: viewer ? {
      id: viewer.id,
      src: viewer.src,
      style: viewer.style.cssText,
      computedStyle: {
        display: getComputedStyle(viewer).display,
        visibility: getComputedStyle(viewer).visibility,
        opacity: getComputedStyle(viewer).opacity,
        width: getComputedStyle(viewer).width,
        height: getComputedStyle(viewer).height
      },
      attributes: Array.from(viewer.attributes).map(attr => `${attr.name}="${attr.value}"`),
      classList: Array.from(viewer.classList)
    } : null,
    container: container ? {
      id: container.id,
      style: container.style.cssText,
      classList: Array.from(container.classList)
    } : null,
    stickyContainer: stickyContainer ? {
      id: stickyContainer.id,
      style: stickyContainer.style.cssText
    } : null,
    placeholder: placeholder ? {
      children: placeholder.children.length,
      classList: Array.from(placeholder.classList),
      hasMovedViewer: placeholder.querySelector('.moved-to-custom') ? true : false
    } : null
  });
  
  // Try to force viewer refresh
  if (viewer) {
    console.log('🔄 Forcing viewer refresh...');
    window.dispatchEvent(new Event('resize'));
    
    if (viewer.updateComplete) {
      viewer.updateComplete.then(() => console.log('✅ Viewer update complete'));
    }
    
    // Try to reload the model
    if (viewer.src) {
      const currentSrc = viewer.src;
      viewer.src = '';
      setTimeout(() => {
        viewer.src = currentSrc;
        console.log('🔄 Viewer src reloaded');
      }, 100);
    }
  }
  
  return { viewer, container, stickyContainer, placeholder };
};

// Debug logo functionality helper
window.debugLogoFunctionality = function() {
  console.log('📁 Debugging Logo Functionality...');
  
  const configurator = window.configuratorInstance;
  if (!configurator) {
    console.log('❌ No configurator instance found');
    return;
  }
  
  const productType = configurator.productType;
  const logoPrice = (productType === 'bag' || productType === 'heavy-bag') ? '$65' : '$1,850';
  console.log(`🥊 Product Type: ${productType.toUpperCase()} (Logo Price: +${logoPrice})`);
  
  // Check UI elements
  const logoEnabled = configurator.root.querySelector('#logo-enabled');
  const logoUpload = configurator.root.querySelector('#logo-upload');
  const logoPreview = configurator.root.querySelector('#logo-preview');
  const logoPricingNote = configurator.root.querySelector('#logo-pricing-note');
  
  console.log('Logo UI Elements:', {
    logoEnabled: logoEnabled ? { checked: logoEnabled.checked, visible: logoEnabled.style.display !== 'none' } : null,
    logoUpload: logoUpload ? { visible: logoUpload.style.display !== 'none' } : null,
    logoPreview: logoPreview ? { visible: logoPreview.style.display !== 'none' } : null,
    logoPricingNote: logoPricingNote ? { visible: logoPricingNote.style.display !== 'none' } : null
  });
  
  // Check TDA logo elements based on product type
  let logoDropdown = null;
  let logoFileInput = null;
  
  if (productType === 'bag' || productType === 'heavy-bag') {
    // For bags: look for file upload and "Custom Logo" section
    const logoOptions = document.querySelectorAll('[id*="tda-customizer-customization-option-"]');
    for (const option of logoOptions) {
      const title = option.querySelector('.tda-customizer-customization-option-title');
      if (title && title.textContent.toLowerCase().includes('logo')) {
        logoDropdown = option.querySelector('select[id*="tda-customizer-customization-option-dropdown-"]');
        logoFileInput = option.querySelector('input[type="file"]');
        console.log(`✅ Found bag logo section: ${option.id}`);
        if (logoDropdown) {
          console.log('Logo dropdown options:', Array.from(logoDropdown.options).map(opt => `${opt.value}: ${opt.textContent}`));
          console.log('Current selection:', logoDropdown.value);
        }
        if (logoFileInput) {
          console.log(`File input: ${logoFileInput.id}`);
        }
        break;
      }
    }
  } else {
    // For rings: look for dropdown with specific values
    const allDropdowns = document.querySelectorAll('select[id*="tda-"]');
    console.log(`Found ${allDropdowns.length} TDA dropdowns total`);
    
    for (const dropdown of allDropdowns) {
      const options = Array.from(dropdown.options);
      const hasLogoOptions = options.some(option => 
        option.textContent.toLowerCase().includes('logo') ||
        option.value === '8wvsxv' ||
        option.value === '0zxrjp'
      );
      
      if (hasLogoOptions) {
        logoDropdown = dropdown;
        console.log(`✅ Found ring logo dropdown: ${dropdown.id}`);
        console.log('Logo options:', options.map(opt => `${opt.value}: ${opt.textContent}`));
        console.log('Current selection:', dropdown.value);
        break;
      }
    }
  }
  
  if (!logoDropdown && !logoFileInput) {
    console.log(`❌ No logo elements found for ${productType}`);
    
    // Debug all TDA elements
    const allTDAElements = document.querySelectorAll('[id*="tda-"]');
    console.log('All TDA elements:', Array.from(allTDAElements).map(el => ({
      id: el.id,
      tagName: el.tagName,
      textContent: el.tagName === 'SELECT' ? Array.from(el.options).map(opt => opt.textContent).join(', ') : el.textContent?.substring(0, 50)
    })));
  }
  
  // Test logo toggle functionality
  console.log('🧪 Testing logo toggle...');
  if (logoEnabled && configurator.ui) {
    const currentState = logoEnabled.checked;
    console.log(`Current logo state: ${currentState ? 'ENABLED' : 'DISABLED'}`);
    
    // Test toggle
    logoEnabled.checked = !currentState;
    configurator.ui.handleLogoToggle(!currentState);
    
    setTimeout(() => {
      console.log(`✅ Logo toggle test complete. New state: ${logoEnabled.checked ? 'ENABLED' : 'DISABLED'}`);
      
      // Check if price updated
      const priceElement = configurator.root.querySelector('.price__current');
      if (priceElement) {
        console.log(`💰 Current price: ${priceElement.textContent}`);
      }
    }, 2000);
  }
  
  return {
    configurator,
    productType,
    logoPrice,
    logoEnabled,
    logoUpload,
    logoPreview,
    logoPricingNote,
    logoDropdown,
    logoFileInput,
    allDropdowns: document.querySelectorAll('select[id*="tda-"]')
  };
};

// Quick test for bag logo functionality
window.testBagLogo = function() {
  console.log('🥊 Testing Bag Logo Functionality...');
  
  const configurator = window.configuratorInstance;
  if (!configurator) {
    console.log('❌ No configurator instance found');
    return;
  }
  
  if (configurator.productType !== 'bag' && configurator.productType !== 'heavy-bag') {
    console.log(`⚠️ Product type is ${configurator.productType}, not bag or heavy-bag`);
    return;
  }
  
  // Test bag logo sync directly
  console.log('🧪 Testing bag logo sync...');
  
  // Enable logo
  const enableResult = configurator.ui.syncBagLogoOption(true);
  console.log(`Enable logo result: ${enableResult ? '✅ SUCCESS' : '❌ FAILED'}`);
  
  setTimeout(() => {
    // Check price update
    const priceElement = configurator.root.querySelector('.price__current');
    if (priceElement) {
      console.log(`💰 Price after enable: ${priceElement.textContent}`);
    }
    
    // Disable logo
    const disableResult = configurator.ui.syncBagLogoOption(false);
    console.log(`Disable logo result: ${disableResult ? '✅ SUCCESS' : '❌ FAILED'}`);
    
    setTimeout(() => {
      if (priceElement) {
        console.log(`💰 Price after disable: ${priceElement.textContent}`);
      }
    }, 1000);
  }, 1000);
};

// Force fix texture backgrounds
window.forceFixTextureBackgrounds = function() {
  console.log('🔧 Force fixing texture backgrounds...');
  
  const textureSwatches = document.querySelectorAll('.color-swatch[data-name="Camo"], .color-swatch[data-name="Gold"], .color-swatch[data-name="Platinum"]');
  console.log(`Found ${textureSwatches.length} texture swatches`);
  
  textureSwatches.forEach(swatch => {
    const style = swatch.getAttribute('style');
    const name = swatch.dataset.name;
    console.log(`🎨 Processing ${name} swatch:`, style);
    
    if (style && style.includes('url(')) {
      const urlMatch = style.match(/url\(['"]*([^'"]*)['"]*\)/);
      if (urlMatch) {
        const imageUrl = urlMatch[1];
        console.log(`🖼️ Applying texture: ${imageUrl}`);
        
        // Clear all background styles
        swatch.style.background = '';
        swatch.style.backgroundColor = '';
        swatch.classList.remove('color-camo', 'color-gold', 'color-platinum');
        
        // Force apply texture
        swatch.style.setProperty('background', `url('${imageUrl}')`, 'important');
        swatch.style.setProperty('background-size', 'cover', 'important');
        swatch.style.setProperty('background-position', 'center', 'important');
        swatch.style.setProperty('background-repeat', 'no-repeat', 'important');
        
        console.log(`✅ Applied texture to ${name} swatch`);
      }
    }
  });
};